import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Badge} from '@vio/atlas/Badge'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Toggle} from '@vio/atlas/Toggle'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {hexToRGBAString} from '@vio/core/src/utils/css'

import {useIsSaveFraming} from '../../hooks/useIsSaveFraming'
import {myListMessages} from '../../myListMessages'
import {DesktopIcon} from '../PriceWatchInfoIcon/DesktopIcon'
import {MobileIcon} from '../PriceWatchInfoIcon/MobileIcon'

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400};
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  border-radius: ${cssTheme.layout.radius.md};
`

export const ToggleWrapper = styled.div`
  display: flex;
  flex: 1 auto;
  justify-content: end;
`

export const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
`

export const PulseAnimation = styled.div`
  border-radius: ${cssTheme.layout.radius.rounded};
  box-shadow: 0 0 0 0 ${cssTheme.colors.badge.accent.background};

  &.--should-pulse {
    animation: pulsePW 3s infinite;
  }

  @keyframes pulsePW {
    0% {
      box-shadow: 0 0 0 0
        ${hexToRGBAString(cssTheme.colors.badge.accent.background, 0.7)};
    }

    40% {
      box-shadow: 0 0 0 7px
        ${hexToRGBAString(cssTheme.colors.badge.accent.background, 0)};
    }

    100% {
      box-shadow: 0 0 0 0
        ${hexToRGBAString(cssTheme.colors.badge.accent.background, 0)};
    }
  }
`

interface PriceWatchToggleProps {
  isToggleOn: boolean
  handleClick: (event: React.SyntheticEvent<Element, Event>) => void
  shouldPulse?: boolean
  hideIcon?: boolean
  hideInfoIcon?: boolean
  className?: string
  /** Holds the label on alert wording. The icon follows the experiment either way. */
  hasAlertText?: boolean
}

export const PriceWatchToggle = ({
  isToggleOn,
  handleClick,
  shouldPulse = true,
  hideIcon = false,
  hideInfoIcon = false,
  className,
  hasAlertText = false
}: PriceWatchToggleProps) => {
  const {isMobile} = useDeviceLayout()
  const saveFramingIsOn = useIsSaveFraming()
  // The mobile AP holds the label on alert wording while the icon stays a
  // heart, so these two deliberately disagree there.
  const hasSaveText = saveFramingIsOn && !hasAlertText
  // Control shows the same bell whether or not the alert is on, so only the
  // save framing fills its icon.
  const savedIcon = isToggleOn ? ('HeartFilled' as const) : ('Heart' as const)
  const badgeIcon = saveFramingIsOn ? savedIcon : ('Bell' as const)

  return (
    <>
      <Wrapper className={className}>
        {!hideIcon && (
          <PulseAnimation className={cx(shouldPulse && '--should-pulse')}>
            <Badge size="md" variant="accent" iconName={badgeIcon} />
          </PulseAnimation>
        )}

        <TextWrapper>
          <Text variant="bodyS">
            {hasSaveText ? (
              <FormattedMessageWrapper {...myListMessages.toggle} />
            ) : (
              <FormattedMessageWrapper
                id="priceWatchDetailedToggle.alertMessage"
                defaultMessage="Alert me when the price changes"
              />
            )}
          </Text>
          {!hideInfoIcon && (isMobile ? <MobileIcon /> : <DesktopIcon />)}
        </TextWrapper>

        <ToggleWrapper>
          <Toggle
            checked={isToggleOn}
            id="PriceWatchToggle"
            onChange={handleClick}
            dataId="PriceWatchToggle"
          />
        </ToggleWrapper>
      </Wrapper>
    </>
  )
}
